import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { filtersToQueryString, type SearchFilters } from '../../model/SearchFilters';
import RouteEnum from '../../RouteEnum';
import { useRouteContext } from '../../RouteProvider';
import Tracking from '../../services/Tracking';
import type { ReduxState } from '../../store';
import { StaticImage } from '../Image';
import { JourneyBlock } from '../Layout/JourneyBlock';
import MobileAppPromo from '../Layout/MobileAppPromo';
import { USPBlock } from '../Layout/USPBlock';
import Link from '../Link';
import MetaTags from '../MetaTags';
import T from '../Translate';

import HomepageHero from './HomepageHero';
import VehicleFeed from './VehicleFeed';
import WelcomeBanner from './WelcomeBanner';

import styles from './homepage.module.scss';

export default function Homepage() {
  const { navigate, generatePath } = useRouteContext();
  const { initialFilters } = useSelector((store: ReduxState) => ({
    initialFilters: store.rentCar.initialFilters,
  }));

  const onSearch = useCallback(
    (filters: Partial<SearchFilters>) => {
      navigate(generatePath(RouteEnum.RENT_CAR, {}, filtersToQueryString(filters, initialFilters)));
    },
    [navigate, initialFilters],
  );

  return (
    <div className={cn('main-container', styles.container)}>
      <MetaTags description="homepage.meta.description" title="homepage.meta.title" />

      <HomepageHero onSearch={onSearch} />

      <WelcomeBanner className={styles.welcomeBanner} />

      <VehicleFeed className={styles.feed} />

      <USPBlock image="hp_usp_1">
        <StaticImage alt="" className={styles.stars} loading="lazy" name="hp_rating" />
        <T as="h2" html id="homepage.usp.1.title" />
        <T as="p" id="homepage.usp.1.description" />
      </USPBlock>

      <USPBlock image="hp_usp_2" left>
        <T as="h2" html id="homepage.usp.2.title" />
        <T as="p" id="homepage.usp.2.description" />
      </USPBlock>

      <USPBlock image="hp_usp_3">
        <T as="h2" html id="homepage.usp.3.title" />
        <T as="p" id="homepage.usp.3.description" />
        <Link
          className="button button-link button-icon button-nopadding"
          onClick={() => Tracking.track('HP_OWNER_SECTION_OFFER_CLICKED')}
          to={RouteEnum.STATIC_OFFER}
        >
          <T id="homepage.usp.3.link" />
          <i className="icon icon-arrow-right-circle icon-lg" />
        </Link>
      </USPBlock>

      <MobileAppPromo />

      <JourneyBlock page="homepage" />
    </div>
  );
}
